// src/components/UserTransferModule.jsx
import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from '../api/axios';
import AuthContext from '../context/AuthContext';

const UserTransferModule = () => {
    const { auth } = useContext(AuthContext);
    const [tenants, setTenants] = useState([]);
    
    // Stati Selezione Tenant
    const [leftTenantId, setLeftTenantId] = useState('');
    const [rightTenantId, setRightTenantId] = useState('');

    // Stati Liste Utenti (Dati grezzi dal DB)
    const [leftUsers, setLeftUsers] = useState([]);
    const [rightUsers, setRightUsers] = useState([]);
    
    // Stati Ricerca Sincrona
    const [leftSearch, setLeftSearch] = useState('');
    const [rightSearch, setRightSearch] = useState('');

    // Stati Selezione Multipla
    const [selectedLeft, setSelectedLeft] = useState([]);
    const [loading, setLoading] = useState(false);

    // 1. Carica Tenants all'avvio
    useEffect(() => {
        let isMounted = true;
        axios.get(`/api_tenants.php?role=${auth.role}`).then(res => {
            if (isMounted && res.data.success) setTenants(res.data.data);
        });
        return () => { isMounted = false; };
    }, [auth.role]);

    // 2. Funzione Fetch Memorizzata (per evitare warning ESLint)
    const fetchUsersForTenant = useCallback(async (tenantId, side) => {
        if (!tenantId) {
            if (side === 'left') setLeftUsers([]);
            if (side === 'right') setRightUsers([]);
            return;
        }
        try {
            const res = await axios.get(`/api_users.php?role=${auth.role}&tenant_id=${tenantId}&limit=1000`);
            if (res.data.success) {
                if (side === 'left') setLeftUsers(res.data.data);
                if (side === 'right') setRightUsers(res.data.data);
            }
        } catch (e) { console.error(e); }
    }, [auth.role]);

    // Trigger fetch quando cambiano i tenant
    useEffect(() => { 
        fetchUsersForTenant(leftTenantId, 'left'); 
    }, [leftTenantId, fetchUsersForTenant]);

    useEffect(() => { 
        fetchUsersForTenant(rightTenantId, 'right'); 
    }, [rightTenantId, fetchUsersForTenant]);

    // 3. Filtri Sincroni (Search Bar)
    const filteredLeftUsers = leftUsers.filter(u => 
        u.name.toLowerCase().includes(leftSearch.toLowerCase()) || 
        u.email.toLowerCase().includes(leftSearch.toLowerCase())
    );

    const filteredRightUsers = rightUsers.filter(u => 
        u.name.toLowerCase().includes(rightSearch.toLowerCase()) || 
        u.email.toLowerCase().includes(rightSearch.toLowerCase())
    );

    // 4. Gestione Toggle Selezione
    const toggleSelect = (userId) => {
        if (selectedLeft.includes(userId)) {
            setSelectedLeft(selectedLeft.filter(id => id !== userId));
        } else {
            setSelectedLeft([...selectedLeft, userId]);
        }
    };

    // 5. Logica API Trasferimento
    const executeTransfer = async (userIds, targetTenantId) => {
        setLoading(true);
        try {
            await axios.post('/api_user_transfer.php', JSON.stringify({
                role: auth.role,
                target_tenant_id: targetTenantId,
                user_ids: userIds
            }));
            
            // Ricarica le liste per vedere lo spostamento
            await fetchUsersForTenant(leftTenantId, 'left');
            await fetchUsersForTenant(rightTenantId, 'right');
            setSelectedLeft([]); // Reset selezione
        } catch (error) {
            alert("Errore durante il trasferimento.");
        } finally {
            setLoading(false);
        }
    };

    // Handler Bottone Centrale
    const moveSelectedToRight = () => {
        if (selectedLeft.length === 0 || !rightTenantId) return;
        executeTransfer(selectedLeft, rightTenantId);
    };

    // Handler Doppio Click (Bidirezionale)
    const handleDoubleClick = (user, direction) => {
        if (direction === 'toRight' && rightTenantId) {
            executeTransfer([user.id], rightTenantId);
        } else if (direction === 'toLeft' && leftTenantId) {
            executeTransfer([user.id], leftTenantId);
        }
    };

    return (
        <div className="container-fluid py-2" style={{ fontSize: '0.85rem' }}>
            <div className="row g-3">
                
                {/* --- COLONNA SINISTRA (DA) --- */}
                <div className="col-md-5">
                    <label className="fw-bold mb-1 text-muted small">Da Tenant:</label>
                    <select 
                        className="form-select form-select-sm mb-2 border-primary shadow-sm" 
                        value={leftTenantId} 
                        onChange={e => setLeftTenantId(e.target.value)}
                    >
                        <option value="">Seleziona...</option>
                        {/* Esclude il tenant selezionato a destra (String comparison per sicurezza) */}
                        {tenants.filter(t => String(t.id) !== String(rightTenantId)).map(t => (
                            <option key={t.id} value={t.id}>{t.name} ({t.user_count})</option>
                        ))}
                    </select>

                    <div className="card shadow-sm border-primary" style={{ height: '60vh', minHeight: '300px', maxHeight: '500px' }}>
                        <div className="card-header bg-light p-1">
                            <input 
                                type="text" 
                                className="form-control form-control-sm border-0 bg-transparent" 
                                placeholder="Cerca..."
                                value={leftSearch}
                                onChange={e => setLeftSearch(e.target.value)}
                            />
                        </div>
                        <div className="list-group list-group-flush overflow-auto h-100">
                            {filteredLeftUsers.map(u => (
                                <button 
                                    key={u.id} 
                                    className={`list-group-item list-group-item-action py-1 px-2 d-flex justify-content-between align-items-center ${selectedLeft.includes(u.id) ? 'active' : ''}`}
                                    onClick={() => toggleSelect(u.id)}
                                    onDoubleClick={() => handleDoubleClick(u, 'toRight')}
                                    style={{ fontSize: '0.8rem', borderLeft: 'none', borderRight: 'none' }}
                                    title="Doppio click per spostare a destra"
                                >
                                    <div className="text-truncate w-100">
                                        <div className="fw-bold text-truncate">{u.name}</div>
                                        <small className={`d-block text-truncate ${selectedLeft.includes(u.id) ? 'text-white-50' : 'text-muted'}`}>
                                            {u.email}
                                        </small>
                                    </div>
                                    <i className="bi bi-chevron-right ms-2 small opacity-50"></i>
                                </button>
                            ))}
                            {filteredLeftUsers.length === 0 && <div className="p-3 text-center text-muted small">Nessun utente</div>}
                        </div>
                    </div>
                </div>

                {/* --- COLONNA CENTRALE (CONTROLLI) --- */}
                <div className="col-md-2 d-flex flex-column justify-content-center align-items-center py-2">
                    <button 
                        className="btn btn-primary btn-sm shadow mb-2" 
                        disabled={selectedLeft.length === 0 || !rightTenantId || loading}
                        onClick={moveSelectedToRight}
                    >
                        <i className="bi bi-arrow-right-circle me-1"></i> Sposta {selectedLeft.length > 0 && `(${selectedLeft.length})`}
                    </button>
                    {loading && <div className="spinner-border text-primary spinner-border-sm" role="status"></div>}
                </div>

                {/* --- COLONNA DESTRA (A) --- */}
                <div className="col-md-5">
                    <label className="fw-bold mb-1 text-muted small">A Tenant:</label>
                    <select 
                        className="form-select form-select-sm mb-2 border-success shadow-sm" 
                        value={rightTenantId} 
                        onChange={e => setRightTenantId(e.target.value)}
                    >
                        <option value="">Seleziona...</option>
                        {/* Esclude il tenant selezionato a sinistra */}
                        {tenants.filter(t => String(t.id) !== String(leftTenantId)).map(t => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                    </select>

                    <div className="card shadow-sm border-success" style={{ height: '60vh', minHeight: '300px', maxHeight: '500px' }}>
                        <div className="card-header bg-success p-1 text-white">
                             <input 
                                type="text" 
                                className="form-control form-control-sm border-0 bg-transparent text-white placeholder-white" 
                                placeholder="Cerca..."
                                value={rightSearch}
                                onChange={e => setRightSearch(e.target.value)}
                                style={{ color: 'white' }}
                            />
                        </div>
                        <div className="list-group list-group-flush overflow-auto h-100">
                            {filteredRightUsers.map(u => (
                                <div 
                                    key={u.id} 
                                    className="list-group-item list-group-item-action py-1 px-2"
                                    style={{cursor: 'pointer', fontSize: '0.8rem', borderLeft: 'none', borderRight: 'none'}}
                                    onDoubleClick={() => handleDoubleClick(u, 'toLeft')}
                                    title="Doppio click per riportare indietro"
                                >
                                     <div className="text-truncate w-100">
                                        <div className="fw-bold text-truncate">{u.name}</div>
                                        <small className="text-muted d-block text-truncate">{u.email}</small>
                                    </div>
                                </div>
                            ))}
                             {filteredRightUsers.length === 0 && <div className="p-3 text-center text-muted small">Nessun utente</div>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserTransferModule;